'use client';

import Icon from '@/components/Icon';
import { Portal } from '@/components/Portal';
import { Button } from '@/components/ui/button';
import { X as CloseIcon } from 'react-feather';
// import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import useClaimWarrantyPage from '@/hooks/ClaimWarrantyPage/useClaimWarrantyPage';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, Upload } from 'antd';
import moment from 'moment-timezone';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAddJsLog } from '../Stripe/use-add-js-log';
import classes from './index.module.css';
export const ClaimWarrantyPage = () => {
  // Add form schema
  const formSchema = z.object({
    email: z.string().email(),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    description: z.string().min(1, 'Description is required'),
    number: z.string().min(1, 'Order number is required'),
  });
  const form = useForm({
    resolver: zodResolver(formSchema as any),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      description: '',
      number: '',
    },
  });
  const uploadButton = <div className={classes.uploadButton} />;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [errorState, setErrorState] = useState('');
  const [fileList, setFileList] = useState<any[]>([]);
  const [openMaskDialog, setOpenMaskDialog] = useState(false);

  const { isBusy, handleSubmit, response } = useClaimWarrantyPage({
    afterSubmit: () => {
      form.reset();

      setFileList([]);
    },
  });

  const maybeSuccessInfo = (
    <span className=''>Submitted successfully! We will be in touch within 48 hours via email, stay tuned.</span>
  );

  useEffect(() => {
    if (response && response?.code === '200') {
      setOpenMaskDialog(true);

      // setTimeout(() => {
      //   setOpenMaskDialog(false)
      // }, 3000)
    }
  }, [response]);
  const removeIcon = (
    <Image
      className='removeIcon h-[13px] w-[13px] lg_h-[20px] lg_w-[20px]'
      src='upload-remove.svg'
      alt='remove icon'
      width={20}
      height={20}
    />
  );
  const titleClassName = 'font-roboto-medium font-medium text-[16px] leading-[27px] mb-[10px] lg_mb-[20px]';
  const textClassName = 'font-roboto-light text-[16px] leading-[26px] font-light  mb-[10px] lg_mb-[20px]';
  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const { reqAddJsLog } = useAddJsLog();

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      const images = await Promise.all(fileList.map(file => getBase64(file.originFileObj)));

      if (images.length === 0) {
        setErrorState('Please upload at least one image');

        return;
      }

      handleSubmit({
        ...values,
        images,
      });
    } catch (error) {
      reqAddJsLog({
        variables: {
          input: {
            type: 'claim_warranty_error',
            message: error instanceof Error ? error.message : String(error),
            time: moment().tz('Asia/Shanghai').format('YYYY-MM-DD HH:mm:ss'),
          },
        },
      }).catch((error: any) => {
        console.log(error);
      });
      console.error('Error converting images:', error);
      setErrorState('Error processing images. Please try again.');
    }
  }

  const handlePreview = async (file: any) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }

    setPreviewImage(file.url || file.preview || file.thumbUrl);
    setPreviewOpen(true);
  };

  const handleChange = ({ fileList: newFileList }: { fileList: any[] }) => {
    const notImageFileIndex = newFileList.findIndex((file: any) => {
      return file.type !== 'image/jpeg' && file.type !== 'image/png';
    });

    if (notImageFileIndex > -1) {
      newFileList.splice(notImageFileIndex, 1);
      setErrorState('You can only upload JPG/PNG file!');
    }

    const sizeLimit = 2 * 1024 * 1024;

    const exceedSizeLimitFileIndex = newFileList.findIndex(file => {
      return file.size > sizeLimit;
    });

    if (exceedSizeLimitFileIndex > -1) {
      newFileList.splice(exceedSizeLimitFileIndex, 1);
      setErrorState(`Image must be smaller than ${Math.floor(sizeLimit / 1024 / 1024)}MB!`);
    }

    if (notImageFileIndex === -1 && exceedSizeLimitFileIndex === -1) {
      setErrorState('');
    }

    setFileList(newFileList);
  };

  return (
    <div className='pb-[40px] lg_pb-[80px]'>
      <div className='relative mb-[0] lg_mb-[60px]'>
        <Image
          src='/claim-warranty/banner-m.jpg'
          alt='Claim Warranty'
          width={750}
          height={400}
          priority
          className='h-auto min-h-[200px] w-full object-cover lg_hidden'
        />
        <Image
          src='/claim-warranty/banner.jpg'
          alt='Claim Warranty'
          width={3840}
          height={740}
          priority
          className='hidden h-auto min-h-[370px] w-full object-cover lg_block'
        />

        <div className={cn('absolute left-0 top-0 hidden h-full w-full lg_block')}>
          <div className='container mx-auto flex h-full flex-col items-start justify-center'>
            <div
              className={cn(' font-teko-medium text-[40px] font-medium uppercase leading-[40px] text-white')}
              style={{ textShadow: '0px 2px 10px rgba(0, 0, 0, 0.4)' }}
            >
              Claim Warranty
            </div>
            <p
              className={cn('text-left font-roboto-medium text-[18px] font-medium leading-[21px] text-white')}
              style={{ textShadow: '0px 2px 10px rgba(0, 0, 0, 0.4)' }}
            >
              Your Journey, Our Commitment
            </p>
          </div>
        </div>
      </div>
      <div className='container mx-auto mb-[40px] bg-[#F9F9F9] px-[12px] py-[20px] lg_mb-[0] lg_hidden'>
        <h1 className={cn('font-teko-medium text-[30px] font-medium uppercase leading-[28px] text-black')}>
          Claim Warranty
        </h1>
        <p className={cn('font-roboto-regular text-[16px] font-medium leading-[24px] text-black')}>
          Your Journey, Our Commitment
        </p>
      </div>
      <div className='personalContent mx-auto mt-[40px] px-[12px] lg_mt-0 lg_px-0'>
        <p className='mb-[20px] font-roboto-medium text-[20px] font-medium leading-[28px] lg_mb-[40px]'>
          We aim to make the warranty process as simple and straightforward as possible. If you believe your product
          qualifies for a warranty claim, please follow the steps below.
        </p>

        <div className='mb-[20px] lg_mb-[40px]'>
          <h2 className={titleClassName}>Step 1. Verify Eligibility</h2>

          <p className={textClassName}>
            Make sure your product is registered for the Lifetime Warranty and that the issue is covered under our
            warranty policy.
          </p>
          <p className={textClassName}>
            For more details, please visit our{' '}
            <Link
              href='/warranty'
              className='font-roboto-medium font-medium underline transition-all duration-lg hover_text-orange'
            >
              Lifetime Warranty
            </Link>{' '}
            page.
          </p>
        </div>

        <div className='mb-[20px] lg_mb-[40px]'>
          <h2 className={titleClassName}>Step 2. Prepare Your Information</h2>

          <p className={cn(textClassName, 'mb-0 font-roboto-medium font-medium lg_mb-0')}>Order Number</p>
          <p className={textClassName}>
            Provide the order number from the platform where you purchased your HISEA product. This helps us verify your
            purchase and product details.
          </p>

          <p className={cn(textClassName, 'mb-0 font-roboto-medium font-medium lg_mb-0')}>
            Photos of the Defect or Damage
          </p>
          <p className={textClassName}>
            Take clear photos of the defect or damage. This helps us assess your claim more quickly.
          </p>
        </div>

        <div className='mb-[20px] lg_mb-[40px]'>
          <h2 className={titleClassName}>Step 3. Submit Your Claim</h2>
          <p className={textClassName}>
            Complete the form below with your contact information, order details, and photos of the issue. Our team will
            review your claim and contact you with the next steps.
          </p>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className='space-y-6'
            >
              <div className='grid grid-cols-1 gap-[20px] md_grid-cols-2'>
                <FormField
                  control={form.control}
                  name='email'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={cn(classes.labelClasses, classes.requiredLabel)}>Email Address </FormLabel>
                      <FormControl>
                        <Input
                          placeholder='email@example.com'
                          {...field}
                          className='border-[#E0E0E0] font-roboto-light placeholder_not-italic'
                        />
                      </FormControl>
                      <FormMessage className='text-error font-roboto-regular text-sm font-medium italic text-[#e82f2f]' />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='number'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel
                        className={cn(classes.labelClasses, classes.requiredLabel, 'relative mb-0 lg_mb-[5px]')}
                      >
                        Order Number
                        <p className='absolute bottom-0 left-[115px] hidden whitespace-nowrap font-roboto-light text-[14px] font-light leading-[24px] text-[#e82f2f] lg_block'>
                          (Please enter your order number, not your warranty card number.)
                        </p>
                      </FormLabel>
                      <p className='w-full font-roboto-light text-[14px] font-light leading-[24px] text-[#e82f2f] lg_hidden'>
                        (Please enter your order number, not your warranty card number.)
                      </p>
                      <FormControl>
                        <Input
                          className='border-[#E0E0E0]'
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className='text-error font-roboto-regular text-sm font-medium italic text-[#e82f2f]' />
                    </FormItem>
                  )}
                />
              </div>

              <div className='grid grid-cols-1 gap-[20px] md_grid-cols-2'>
                <FormField
                  control={form.control}
                  name='firstName'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={cn(classes.labelClasses, classes.requiredLabel)}>First Name </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className='border-[#E0E0E0]'
                        />
                      </FormControl>
                      <FormMessage className='text-error font-roboto-regular text-sm font-medium italic text-[#e82f2f]' />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='lastName'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={cn(classes.labelClasses, classes.requiredLabel)}>Last Name </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className='border-[#E0E0E0]'
                        />
                      </FormControl>
                      <FormMessage className='text-error font-roboto-regular text-sm font-medium italic text-[#e82f2f]' />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='description'
                  render={({ field }) => (
                    <FormItem className='md_col-span-2'>
                      <FormLabel className={cn(classes.labelClasses, classes.requiredLabel)}>
                        Description of the Issue{' '}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder='Please describe the issue with your product.'
                          className='min-h-[100px] rounded-none border-[#E0E0E0] font-roboto-light placeholder_not-italic'
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className='text-error font-roboto-regular text-sm font-medium italic text-[#e82f2f]' />
                    </FormItem>
                  )}
                />
              </div>

              <div className='grid grid-cols-1 gap-[20px] md_grid-cols-3'>
                <div className='md_col-span-1'>
                  <Label className={cn(classes.labelClasses, classes.requiredLabel)}>
                    Photos of the Defect or Damage{' '}
                  </Label>
                  <div className='mt-2'>
                    <div className={classes.UploadRoot}>
                      <Upload
                        listType='picture-card'
                        multiple
                        fileList={fileList}
                        beforeUpload={() => false}
                        maxCount={5}
                        onChange={handleChange}
                        onPreview={handlePreview}
                        accept='image/png,image/jpeg'
                        showUploadList={{
                          showDownloadIcon: false,
                          showPreviewIcon: false,
                          showRemoveIcon: true,
                          removeIcon: removeIcon,
                        }}
                      >
                        {fileList.length >= 5 ? null : uploadButton}
                      </Upload>
                    </div>

                    <Modal
                      open={previewOpen}
                      footer={null}
                      onCancel={() => setPreviewOpen(false)}
                    >
                      <Image
                        alt='preview image'
                        style={{
                          width: '100%',
                        }}
                        width={500}
                        height={500}
                        src={previewImage}
                      />
                    </Modal>

                    <span className='text-orange'>{errorState}</span>
                  </div>
                </div>
              </div>
              <p className='mb-[5px] flex items-center justify-start gap-[10px] lg_mb-[10px]'>
                <Image
                  src={`/discount/pic-icon.svg`}
                  alt='Upload Icon'
                  width={22}
                  height={20}
                  className='h-[20px] w-[22px]'
                />
                <span className={cn('font-roboto-medium text-[16px] font-medium leading-[24px]', classes.UploadTip)}>
                  Upload Photos
                </span>
              </p>
              <Button
                type='submit'
                className='duration-300 h-[40px] w-full rounded-full bg-black font-roboto-medium text-[16px] leading-[40px] text-white transition-colors hover_bg-[#4C4C4C] lg_h-[50px] lg_w-[140px] lg_text-[18px] lg_leading-[50px]'
                disabled={isBusy}
              >
                Submit
              </Button>
            </form>
          </Form>
        </div>
        {openMaskDialog && (
          <Portal>
            <aside className='fixed left-1/2 top-1/2 z-[100] w-[calc(100vw-36px)] -translate-x-1/2 -translate-y-1/2 transform bg-white lg_w-[800px]'>
              <div className='relative h-full w-full  pt-[46px] '>
                <Icon
                  className='absolute right-[15px] top-[15px] cursor-pointer'
                  src={CloseIcon}
                  size={26}
                  onClick={() => setOpenMaskDialog(false)}
                />
                <div className='border-b-1 border-solid border-[#E0E0E0] px-[20px] pb-[20px] lg_px-[80px] lg_pb-[40px]'>
                  <Image
                    className='mx-auto mb-[20px]'
                    src={'/contact-us/success.svg'}
                    alt='success'
                    width={75.5}
                    height={70}
                  />
                  <p className='mb-[20px] text-center font-roboto-medium text-[20px] leading-[28px] lg_text-left lg_text-center'>
                    Submitted Successfully
                  </p>
                  <p className=' text-left font-roboto-light text-[16px] font-light leading-[26px]  lg_text-center'>
                    Check your email to find the confirmation email.
                    <br /> Usually it will arrive within 3 minutes.
                  </p>
                  <p className='mt-[10px] text-left font-roboto-light text-[16px] font-light leading-[26px] lg_text-center'>
                    Please add info@hisea.com to your email contacts to avoid missing our important emails.
                  </p>
                </div>
              </div>
            </aside>
            <div
              className='fixed left-0 top-0 z-[99] h-[100vh] w-[100vw] bg-black opacity-60 '
              onClick={() => setOpenMaskDialog(false)}
            ></div>
          </Portal>
        )}
        {/* <div className='mt-[20px] bg-[#f8f8f8] px-[20px] py-[30px] lg_mt-[40px] lg_px-[55px] lg_py-[50px]'>
          <h2 className={titleClassName}>Step4. Claim Approval and Next Steps</h2>
          <p className='mt-[30px]'>We’ll review your claim and get back to you within 1- 3 business days.</p>
          <p className='mt-[30px]'>
            Once approved, we’ll replace your product at no cost. If the product you purchased is discontinued, we’ll
            refund.
          </p>

          <p className='mt-[50px]'>If you have further doubts, don’t hesitate to contact us at cs@hisea.com</p>
        </div> */}

        {/* <Dialog
          open={openMaskDialog}
          onOpenChange={setOpenMaskDialog}
        >
          <DialogContent
            className='max-w-[96vw] text-center lg_max-w-[600px] lg_p-[40px]'
            closeClassName='absolute top-[-2rem] right-0 text-white'
            xClassName='text-white w-[30px] h-[30px]'
          >
            <div className='flex flex-col items-center justify-center gap-[18px]'>
              <CircleCheckBig className='h-[30px] w-[30px] text-primary' />
              <p className='text-[18px]'>{maybeSuccessInfo}</p>
            </div>

            <LinkButton
              href='/'
              className='mt-[30px] h-[50px] w-full font-oswald-bold uppercase'
            >
              back to shopping
            </LinkButton>
          </DialogContent>
        </Dialog> */}
      </div>
    </div>
  );
};

function UploadIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='34'
      height='34'
      fill='none'
      viewBox='0 0 34 34'
    >
      <mask
        id='mask0_10259_42065'
        style={{
          maskType: 'luminance',
        }}
        width='34'
        height='34'
        x='0'
        y='0'
        maskUnits='userSpaceOnUse'
      >
        <path
          fill='#fff'
          d='M34 0H.007v34H34V0z'
        />
      </mask>
      <g mask='url(#mask0_10259_42065)'>
        <path
          fill='#8EA0AA'
          d='M33.99 20.898h-2.615v10.447H2.615V20.898H0v13.059h33.99V20.898z'
        />
      </g>
      <path
        fill='#8EA0AA'
        d='M18.302 1.313h-2.614v24.81h2.614V1.314z'
      />
      <path
        fill='#8EA0AA'
        d='M17.518.527l-1.83 1.829 7.582 7.312L25.1 7.84 17.518.527z'
      />
      <path
        fill='#8EA0AA'
        d='M16.996.004L9.152 7.839l1.83 1.829 7.844-7.835-1.83-1.83z'
      />
    </svg>
  );
}
